import axios from 'axios';
import { LanguageObject } from '@/i18n';
import { Survey, SurveyGroup } from './SurveyUtils';
import { ServiceProvider } from './ProviderUtils';
import { Role } from './RoleUtils';
import { User } from './UserUtils';

interface AutomatedEmail {
  id?: number;
  key: string;
  name: string;
  system: boolean;
  subject?: LanguageObject;
  body?: LanguageObject;
  defaultsignature?: Partial<AutomatedEmail> | null;
  signature?: LanguageObject | null;
  type: 'user' | 'organization' | 'survey' | 'signature';
}

interface AutomatedEmailSummary {
  emailcount: number;
  usercount: number;
}

interface AutomatedEmailParams {
  emailid?: number; // email id
  surveygroup?: Partial<SurveyGroup>;
  surveys?: Partial<Survey>[];
  serviceproviders?: Partial<ServiceProvider>[];
  roles?: Partial<Role>[];
  users?: Partial<User>[];
}

const api = {
  list: async (params?: { type?: AutomatedEmail['type'] }) => {
    const resp = await axios.get<AutomatedEmail[]>(
      '/automatedEmails/list.php',
      { params: params }
    );
    return resp.data;
  },

  get: async (id: number) => {
    const resp = await axios.get<AutomatedEmail>('/automatedEmails/get.php', {
      params: { id: id },
    });
    return resp.data;
  },

  add: async (email: Partial<AutomatedEmail>) => {
    const resp = await axios.post<AutomatedEmail>(
      '/automatedEmails/add.php',
      email
    );
    return resp.data;
  },

  update: async (email: Partial<AutomatedEmail>) => {
    const resp = await axios.post<AutomatedEmail>(
      '/automatedEmails/update.php',
      email
    );
    return resp.data;
  },

  remove: async (id: number) => {
    await axios.delete('/automatedEmails/delete.php', {
      params: { id: id },
    });
  },

  summary: async (automatedEmailParams: AutomatedEmailParams) => {
    const resp = await axios.post<AutomatedEmailSummary>(
      '/automatedEmails/summary.php',
      automatedEmailParams
    );
    return resp.data;
  },

  send: async (
    params: AutomatedEmailParams,
    test: boolean = false,
    multiLingual: boolean = false
  ) => {
    const resp = await axios.post<any>('/automatedEmails/send.php', {
      ...packageAutomatedEmailParams(params),
      test: test,
      multiLingual: multiLingual,
    });
    return resp.data;
  },
};

const generateAutomatedEmailParams = (
  email?: number | AutomatedEmail
): AutomatedEmailParams => {
  return {
    emailid: typeof email === 'object' ? email?.id : email,
    surveygroup: undefined,
    surveys: undefined,
    serviceproviders: undefined,
    roles: undefined,
    users: undefined,
  };
};

const packageAutomatedEmailParams = (
  params: AutomatedEmailParams
): AutomatedEmailParams => {
  return {
    emailid: params.emailid,
    surveygroup: params.surveygroup
      ? { id: params.surveygroup?.id }
      : undefined,
    surveys: params.surveys
      ? params.surveys.map((survey) => ({
          id: survey?.id,
        }))
      : undefined,
    serviceproviders: params.serviceproviders
      ? params.serviceproviders.map((serviceprovider) => ({
          id: serviceprovider?.id,
        }))
      : undefined,
    roles: params.roles
      ? params.roles.map((role) => ({
          id: role?.id,
        }))
      : undefined,
    users: params.users
      ? params.users.map((user) => ({ id: user.id }))
      : undefined,
  };
};

export default {
  api,
  generateAutomatedEmailParams,
};

export { AutomatedEmail, AutomatedEmailParams, AutomatedEmailSummary };
